import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { FileSizeGuard } from './components/FileSizeGuard';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { ContractsList } from './pages/ContractsList';
import { ContractForm } from './pages/ContractForm';
import { ContractDetail } from './pages/ContractDetail';
import { ContractsAllPage } from './pages/ContractsAllPage';
import PartnersPage from './pages/PartnersPage';
import PartnerManagePage from './pages/PartnerManagePage';
import { DataMigrationsPage } from './pages/DataMigrationsPage';
import { UsersList } from './pages/UsersList';
import { TicketsList } from './pages/TicketsList';
import { TicketDetail } from './pages/TicketDetail';
import { CalendarPage } from './pages/CalendarPage';
import { VerifyIberdrolaPage } from './pages/VerifyIberdrolaPage';
import { PartnerConversionsPage } from './pages/PartnerConversionsPage';
import { KnowledgeBase } from './pages/KnowledgeBase';
import { ActivitiesPage } from './pages/ActivitiesPage';
import ProfilePage from './pages/ProfilePage';
import PushStatusPage from './pages/PushStatusPage';
import CommandCenterPage from './pages/CommandCenterPage';
import MetasPage from './pages/MetasPage';
import ChatPage from './pages/ChatPage';
import { PartnersDashboard } from './pages/PartnersDashboard';
import { ComercialsDashboard } from './pages/ComercialsDashboard';
import { AngariadoresDashboard } from './pages/AngariadoresDashboard';
import ProductionReportPage from './pages/ProductionReportPage';
import { PartnerDetailPage } from './pages/PartnerDetailPage';
import { ComercialDetailPage } from './pages/ComercialDetailPage';
import { LeadsPage } from './pages/LeadsPage';
import { OportunidadesPage } from './pages/OportunidadesPage';
import LeadManagementPage from './pages/LeadManagementPage';
import Client360Page from './pages/Client360Page';
import PendenciasPage from './pages/PendenciasPage';
import Comercial360Page from './pages/Comercial360Page';
import Empresa360Page from './pages/Empresa360Page';
import { ChargebackRulesPage } from './pages/ChargebackRulesPage';
import { FidelizacaoRulesPage } from './pages/FidelizacaoRulesPage';
import { SlugViolationsPage } from './pages/SlugViolationsPage';
import { CampaignsAdminPage } from './pages/CampaignsAdminPage';
import { TarifariosAdminPage } from './pages/TarifariosAdminPage';
import { SimuladorPage } from './pages/SimuladorPage';
import { LeadPipelinePage } from './pages/LeadPipelinePage';
import { LeadDetailPage } from './pages/LeadDetailPage';
import PublicSignPage from './pages/PublicSignPage';
import HelpCenterPage from './pages/HelpCenterPage';import { LeadImportPage } from './pages/LeadImportPage';
import { LeadsPerformancePage } from './pages/LeadsPerformancePage';
import { LeadsStatsPage } from './pages/LeadsStatsPage';
import DailyReportPage from './pages/DailyReportPage';
import FollowUpsFaturaPage from './pages/FollowUpsFaturaPage';
import D45FollowUpsPage from './pages/D45FollowUpsPage';
import RecognitionsWallPage from './pages/RecognitionsWallPage';
import { InvitesPage } from './pages/InvitesPage';
import { FinancePage } from './pages/FinancePage';
import { ImportPage } from './pages/ImportPage';
import LeadPublicForm from './pages/LeadPublicForm';
import InviteRegisterForm from './pages/InviteRegisterForm';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F3F4F6]">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Angariador redirect: if angariador tries to access /dashboard, redirect to /leads
const DashboardRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F3F4F6]">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === 'angariador') {
    return <Navigate to="/leads" replace />;
  }

  return children;
};

// Public Route Component (redirects to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F3F4F6]">
        <div className="spinner" />
      </div>
    );
  }

  if (user) {
    return <Navigate to={['super_admin', 'gestor'].includes(user.role) ? '/centro-comando' : '/dashboard'} replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <DashboardRoute>
            <Dashboard />
          </DashboardRoute>
        }
      />

      {/* Partners Route */}
      <Route
        path="/centro-ajuda"
        element={
          <ProtectedRoute>
            <HelpCenterPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/parceiros"
        element={
          <ProtectedRoute>
            <PartnersDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/parceiros/:partnerId"
        element={
          <ProtectedRoute>
            <PartnerDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/comerciais"
        element={
          <ProtectedRoute>
            <ComercialsDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/comerciais/:userId"
        element={
          <ProtectedRoute>
            <ComercialDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/angariadores"
        element={
          <ProtectedRoute>
            <AngariadoresDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/relatorios"
        element={
          <ProtectedRoute>
            <ProductionReportPage />
          </ProtectedRoute>
        }
      />

      {/* Partners Route */}
      <Route
        path="/partners"
        element={
          <ProtectedRoute>
            <PartnersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/partners/:partnerId"
        element={
          <ProtectedRoute>
            <PartnerManagePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/data-migrations"
        element={
          <ProtectedRoute>
            <DataMigrationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/tarifarios"
        element={
          <ProtectedRoute>
            <TarifariosAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/simulador"
        element={
          <ProtectedRoute>
            <SimuladorPage />
          </ProtectedRoute>
        }
      />

      {/* Contracts Routes - All contracts view */}
      <Route
        path="/contracts/all"
        element={
          <ProtectedRoute>
            <ContractsAllPage />
          </ProtectedRoute>
        }
      />

      {/* Contracts Routes - Category/Company structure */}
      <Route
        path="/contracts/:category/:company"
        element={
          <ProtectedRoute>
            <ContractsList />
          </ProtectedRoute>
        }
      />

      <Route
        path="/contracts/:category/:company/new"
        element={
          <ProtectedRoute>
            <ContractForm />
          </ProtectedRoute>
        }
      />

      <Route
        path="/contracts/:category/:company/:id"
        element={
          <ProtectedRoute>
            <ContractDetail />
          </ProtectedRoute>
        }
      />

      <Route
        path="/contracts/:category/:company/:id/edit"
        element={
          <ProtectedRoute>
            <ContractForm />
          </ProtectedRoute>
        }
      />

      {/* Users */}
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            <UsersList />
          </ProtectedRoute>
        }
      />

      {/* Teams - redirect to partners */}
      <Route
        path="/teams"
        element={<Navigate to="/partners" replace />}
      />

      {/* Tickets */}
      <Route
        path="/tickets"
        element={
          <ProtectedRoute>
            <TicketsList />
          </ProtectedRoute>
        }
      />

      <Route
        path="/tickets/new"
        element={
          <ProtectedRoute>
            <TicketsList />
          </ProtectedRoute>
        }
      />

      <Route
        path="/tickets/:id"
        element={
          <ProtectedRoute>
            <TicketDetail />
          </ProtectedRoute>
        }
      />

      <Route path="/centro-comando" element={<ProtectedRoute><CommandCenterPage /></ProtectedRoute>} />
      <Route path="/metas" element={<ProtectedRoute><MetasPage /></ProtectedRoute>} />
      <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route
        path="/avisos-telefone"
        element={
          <ProtectedRoute>
            <PushStatusPage />
          </ProtectedRoute>
        }
      />

      {/* Calendar */}
      <Route
        path="/calendar"
        element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        }
      />

      {/* Tools — Iberdrola NIF eligibility check */}
      <Route
        path="/tools/verify-iberdrola"
        element={
          <ProtectedRoute>
            <VerifyIberdrolaPage />
          </ProtectedRoute>
        }
      />

      {/* Partner restricted view — Conversions list */}
      <Route
        path="/parceiro/conversoes"
        element={
          <ProtectedRoute>
            <PartnerConversionsPage />
          </ProtectedRoute>
        }
      />

      {/* Knowledge Base */}
      <Route
        path="/knowledge"
        element={
          <ProtectedRoute>
            <KnowledgeBase />
          </ProtectedRoute>
        }
      />

      {/* Activities */}
      <Route
        path="/activities"
        element={
          <ProtectedRoute>
            <ActivitiesPage />
          </ProtectedRoute>
        }
      />

      {/* Settings */}
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Leads */}
      <Route
        path="/leads"
        element={
          <ProtectedRoute>
            <LeadsPage />
          </ProtectedRoute>
        }
      />

      <Route path="/clientes/360" element={<ProtectedRoute><Client360Page /></ProtectedRoute>} />
      <Route path="/pendencias" element={<ProtectedRoute><PendenciasPage /></ProtectedRoute>} />
      <Route path="/comerciais/:id/360" element={<ProtectedRoute><Comercial360Page /></ProtectedRoute>} />
      <Route path="/empresas/360" element={<ProtectedRoute><Empresa360Page /></ProtectedRoute>} />
      <Route path="/empresas/360/:company" element={<ProtectedRoute><Empresa360Page /></ProtectedRoute>} />

      {/* Oportunidades de troca */}
      <Route
        path="/oportunidades"
        element={
          <ProtectedRoute>
            <OportunidadesPage />
          </ProtectedRoute>
        }
      />

      {/* Chargeback rules - super_admin only */}
      <Route
        path="/admin/chargeback-rules"
        element={
          <ProtectedRoute>
            <ChargebackRulesPage />
          </ProtectedRoute>
        }
      />

      {/* Fidelização rules - super_admin only */}
      <Route
        path="/admin/fidelizacao-rules"
        element={
          <ProtectedRoute>
            <FidelizacaoRulesPage />
          </ProtectedRoute>
        }
      />

      {/* Slug Violations audit - super_admin only */}
      <Route
        path="/admin/slug-violations"
        element={
          <ProtectedRoute>
            <SlugViolationsPage />
          </ProtectedRoute>
        }
      />

      {/* Campaigns admin - super_admin + gestor */}
      <Route
        path="/admin/campanhas"
        element={
          <ProtectedRoute>
            <CampaignsAdminPage />
          </ProtectedRoute>
        }
      />

      {/* Lead Pipeline (comercial workspace) */}
      <Route path="/leads/pipeline" element={<ProtectedRoute><LeadPipelinePage /></ProtectedRoute>} />
      <Route path="/leads/importar" element={<ProtectedRoute><LeadImportPage /></ProtectedRoute>} />
      <Route path="/leads/performance" element={<ProtectedRoute><LeadsPerformancePage /></ProtectedRoute>} />
      <Route path="/leads/stats" element={<ProtectedRoute><LeadsStatsPage /></ProtectedRoute>} />
      <Route path="/leads/follow-ups-fatura" element={<ProtectedRoute><FollowUpsFaturaPage /></ProtectedRoute>} />
      <Route path="/leads/follow-ups-d45" element={<ProtectedRoute><D45FollowUpsPage /></ProtectedRoute>} />
      <Route path="/leads/relatorio-diario" element={<ProtectedRoute><DailyReportPage /></ProtectedRoute>} />
      <Route path="/leads/gestao" element={<ProtectedRoute><LeadManagementPage /></ProtectedRoute>} />
      <Route path="/leads/:id" element={<ProtectedRoute><LeadDetailPage /></ProtectedRoute>} />

      {/* Personal Wall of Fame */}
      <Route path="/recognitions/mine" element={<ProtectedRoute><RecognitionsWallPage /></ProtectedRoute>} />

      {/* Invites - redirect to partners */}
      <Route
        path="/invites"
        element={<Navigate to="/partners" replace />}
      />

      {/* Financeiro */}
      <Route
        path="/financeiro"
        element={
          <ProtectedRoute>
            <FinancePage />
          </ProtectedRoute>
        }
      />

      {/* Import */}
      <Route
        path="/importar"
        element={
          <ProtectedRoute>
            <ImportPage />
          </ProtectedRoute>
        }
      />

      {/* Public routes (NO AUTH) */}
      <Route path="/l/:referralCode" element={<LeadPublicForm />} />
      <Route path="/lead/:referralCode" element={<LeadPublicForm />} />
      <Route path="/convite/:inviteCode" element={<InviteRegisterForm />} />
      <Route path="/sign/:token" element={<PublicSignPage />} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <FileSizeGuard />
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: '#fff',
              border: '1px solid #E2E8F0',
              borderRadius: '12px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
